import type { Team } from "@repo/types";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { Button } from "@repo/ui/components/Base/Button";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MoreHorizontal,
} from "lucide-react";
import {
  createCreatedAtColumn,
  createCreatedByColumn,
  createIdColumn,
  createNameColumn,
  createUpdatedAtColumn,
  createUpdatedByColumn,
} from "../dataTable/columns";
import type { TableLayoutState } from "@repo/ui/components/Base/DataTable";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useMemo } from "react";

export const useColumns = ({
  tenantId,
  handleQuickEdit,
  handleEdit,
  handleDelete,
}: {
  tenantId: string;
  handleQuickEdit: (id: string) => void;
  handleEdit: (id: string) => void;
  handleDelete: (id: string) => void;
}) => {
  handleQuickEdit = useFutureCallback(handleQuickEdit);
  handleEdit = useFutureCallback(handleEdit);
  handleDelete = useFutureCallback(handleDelete);

  const col: ColumnDef<Team>[] = useMemo(
    () => [
      {
        id: "expander",
        header: () => null,
        meta: {
          className: "w-7",
        },
        size: 30,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <Button size="icon" onClick={row.getToggleExpandedHandler()}>
              {row.getIsExpanded() ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </Button>
          ) : (
            " "
          );
        },
      },
      {
        id: "actions",
        header: "",
        meta: {
          className: "w-7",
        },
        cell: ({ row }) => {
          const team = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleQuickEdit(team.id)}>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleEdit(team.id)}>
                  Manage Members
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDelete(team.id)}>
                  Remove
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigator.clipboard.writeText(team.id)}
                >
                  Copy Team ID
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableHiding: false,
      },
      createIdColumn(),
      createNameColumn(),
      createCreatedAtColumn(),
      createCreatedByColumn(tenantId),
      createUpdatedAtColumn(),
      createUpdatedByColumn(tenantId),
    ],
    [tenantId, handleQuickEdit, handleEdit, handleDelete]
  );
  return col;
};

export const layoutDefaults: TableLayoutState = {
  columnVisibility: {
    id: false,
    createdBy: false,
    updatedAt: false,
    updatedBy: false,
  },
};
